// javascript
// app/javascript/controllers/p220/chat_widget_controller.js
//
// Stimulus controller for the P220 floating AI chat widget.
// Handles open/close of the chat panel, initial turbo-frame loading,
// new conversation creation, history navigation, and a "wide" mode that
// expands the panel into the available right-side area without covering
// the page navigation (NOT browser fullscreen).
import { Controller } from "@hotwired/stimulus"

const SIZE_NORMAL = { width: "min(630px, 100vw)", height: "min(85vh, 900px)" }
const SIZE_WIDE   = { width: "min(65vw, 1200px)", height: "min(92vh, 1400px)" }

export default class extends Controller {
  static targets = ["panel", "toggleBtn", "chatFrame", "wideIcon", "narrowIcon"]
  static values = {
    conversationsUrl: String,
    createUrl: String,
    open: { type: Boolean, default: false },
    wide: { type: Boolean, default: false }
  }

  connect() {
    // Restore open state from sessionStorage
    const stored = sessionStorage.getItem("p220_chat_open")
    if (stored === "true") {
      this.openValue = true
      this._showPanel()
      this._ensureLoaded()
    }

    // Restore wide state
    const storedWide = sessionStorage.getItem("p220_chat_wide")
    this.wideValue = storedWide === "true"
    this._applyPanelSize()
  }

  toggle() {
    this.openValue = !this.openValue
    sessionStorage.setItem("p220_chat_open", this.openValue)

    if (this.openValue) {
      this._showPanel()
      this._ensureLoaded()
    } else {
      this._hidePanel()
    }
  }

  close() {
    this.openValue = false
    sessionStorage.setItem("p220_chat_open", "false")
    this._hidePanel()
  }

  toggleWide() {
    this.wideValue = !this.wideValue
    sessionStorage.setItem("p220_chat_wide", String(this.wideValue))
    this._applyPanelSize()
  }

  newConversation(event) {
    event.preventDefault()
    const url = this.createUrlValue
    if (!url) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(url, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html, text/html",
        "X-CSRF-Token": csrfToken
      },
      redirect: "follow"
    }).then(response => {
      if (response.redirected) {
        // Load the redirected conversation page into the turbo frame
        this.chatFrameTarget.src = response.url
      } else if (response.ok) {
        return response.text().then(html => {
          // If turbo stream, let Turbo handle it
          if (response.headers.get("content-type")?.includes("turbo-stream")) {
            Turbo.renderStreamMessage(html)
          }
        })
      }
    }).catch(err => {
      console.error("[P220 Chat] newConversation error:", err)
    })
  }

  showHistory(event) {
    event.preventDefault()
    const url = this.conversationsUrlValue
    if (url && this.hasChatFrameTarget) {
      this.chatFrameTarget.src = url
    }
  }

  // Private

  _showPanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("hidden")
      // Small delay for animation
      requestAnimationFrame(() => {
        this.panelTarget.classList.add("p220-chat-panel-open")
      })
    }
    if (this.hasToggleBtnTarget) {
      this.toggleBtnTarget.classList.add("hidden")
    }
  }

  _hidePanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("p220-chat-panel-open")
      this.panelTarget.classList.add("hidden")
    }
    if (this.hasToggleBtnTarget) {
      this.toggleBtnTarget.classList.remove("hidden")
    }
  }

  _ensureLoaded() {
    if (this._loaded) return
    this._loaded = true

    // Load conversations list into the turbo frame
    if (this.hasChatFrameTarget && this.conversationsUrlValue) {
      this.chatFrameTarget.src = this.conversationsUrlValue
    }
  }

  _applyPanelSize() {
    if (!this.hasPanelTarget) return
    const size = this.wideValue ? SIZE_WIDE : SIZE_NORMAL
    this.panelTarget.style.width = size.width
    this.panelTarget.style.height = size.height

    // Toggle icon visibility (which arrow to show in the button)
    if (this.hasWideIconTarget) {
      this.wideIconTarget.classList.toggle("hidden", this.wideValue)
    }
    if (this.hasNarrowIconTarget) {
      this.narrowIconTarget.classList.toggle("hidden", !this.wideValue)
    }
  }
}
